// frontend/src/lib/api.js
// ✅ FIXED: Never calls Pi.authenticate — only reads from localStorage
// Pi.authenticate is ONLY called once in App.js

// ---------- Environment Detection ----------

const inSandbox =
  (typeof window !== "undefined" && window !== window.parent) ||
  /app-cdn\.minepi\.com|app\.minepi\.com/i.test(
    (typeof document !== "undefined" && document.referrer) || ""
  );

const isLocal =
  typeof window !== "undefined" &&
  /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(window.location.hostname);

// ---------- Base URL Selection ----------

const DEFAULT_API_BASE = "https://artibytes-api-testnet-191042099955.europe-west4.run.app";

const ENV_BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  process.env.NEXT_PUBLIC_API_BASE ||
  "";

const RUNTIME_BASE =
  typeof window !== "undefined" && window.__ARTIBYTES_API_BASE__
    ? String(window.__ARTIBYTES_API_BASE__)
    : "";

let BASE = "";
if (RUNTIME_BASE) {
  BASE = RUNTIME_BASE;
} else if (ENV_BASE) {
  BASE = ENV_BASE;
} else if (inSandbox) {
  BASE = DEFAULT_API_BASE;
} else if (isLocal) {
  BASE = DEFAULT_API_BASE;
} else {
  BASE = DEFAULT_API_BASE;
}

BASE = BASE.replace(/\/+$/, "");

const withBase = (p) => {
  const path = p.startsWith("/") ? p : `/${p}`;
  return BASE ? `${BASE}${path}` : path;
};

const defaultCreds = (BASE || inSandbox) ? "omit" : "include";

// ---------- Token reading (NO Pi.authenticate calls) ----------

function getPiToken() {
  // ✅ Only read from localStorage — never call Pi.authenticate
  try {
    const stored = localStorage.getItem("artibytes_auth_v1");
    if (stored) {
      const data = JSON.parse(stored);
      if (data?.accessToken) return data.accessToken;
    }
  } catch (e) {
    console.warn("[Pi] Failed to read from localStorage:", e);
  }
  return null;
}

export function clearPiToken() {
  try {
    localStorage.removeItem("artibytes_auth_v1");
  } catch (e) {
    console.warn("[Pi] Failed to clear localStorage:", e);
  }
}

async function getHeaders(customHeaders = {}) {
  const headers = {
    "Content-Type": "application/json",
    "Accept": "application/json",
    ...customHeaders,
  };

  const token = getPiToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
    headers["x-pi-access-token"] = token;
  }

  return headers;
}

// ---------- Response Helpers ----------

async function toJson(res) {
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

async function handle(res) {
  const data = await toJson(res);
  if (!res.ok) {
    const msg = typeof data === "string" ? data : data?.error || res.statusText || "Request failed";
    const err = new Error(`HTTP ${res.status}: ${msg}`);
    err.status = res.status;
    console.error("[API]", err.message);
    throw err;
  }
  return data;
}

// ---------- Public API ----------

export async function apiGet(path, init = {}) {
  const headers = await getHeaders(init.headers);
  const { method, ...restInit } = init;

  const res = await fetch(withBase(path), {
    ...restInit,
    method: "GET",
    credentials: restInit.credentials || defaultCreds,
    headers,
  });

  return handle(res);
}

export async function apiPost(path, body, init = {}) {
  const headers = await getHeaders(init.headers);

  const res = await fetch(withBase(path), {
    method: "POST",
    credentials: init.credentials || defaultCreds,
    headers,
    body: JSON.stringify(body ?? {}),
    ...init,
  });

  return handle(res);
}

export async function apiPatch(path, body, init = {}) {
  const headers = await getHeaders(init.headers);

  const res = await fetch(withBase(path), {
    method: "PATCH",
    credentials: init.credentials || defaultCreds,
    headers,
    body: JSON.stringify(body ?? {}),
    ...init,
  });

  return handle(res);
}

// ✅ Convenience helpers for feedback endpoints
export const feedback = {
  async reportStyle(styleId, { reason, generationId }) {
    return apiPost(`/api/feedback/styles/${styleId}/report`, { reason, generationId });
  },
  async likeStyle(styleId, { generationId }) {
    return apiPost(`/api/feedback/styles/${styleId}/like`, { generationId });
  },
  async getMyStyleReports(status = 'all') {
    return apiGet(`/api/feedback/my-styles/reports?status=${status}`);
  },
  async resolveReport(reportId, action) {
    return apiPatch(`/api/feedback/my-styles/reports/${reportId}/resolve`, { action });
  },
  async getStyleStats(styleId) {
    return apiGet(`/api/feedback/styles/${styleId}/stats`);
  },
  async getBulkStyleStats() {
    return apiGet('/api/feedback/styles/bulk-stats');
  },
};

export const API_BASE = BASE;

export const ENV_INFO = {
  inSandbox,
  isLocal,
  API_BASE: BASE,
  usingProxy: !BASE,
  credentials: defaultCreds,
};

if (typeof window !== "undefined") {
  window.debugArtiBytes = () => {
    console.group("🔍 ArtiBytes API Configuration");
    console.log("In Pi Sandbox:", ENV_INFO.inSandbox);
    console.log("Is Localhost:", ENV_INFO.isLocal);
    console.log("API Base URL:", ENV_INFO.API_BASE || "(using proxy)");
    console.log("Using Proxy:", ENV_INFO.usingProxy);
    console.log("Credentials:", ENV_INFO.credentials);
    console.log("Runtime Override:", RUNTIME_BASE || "(none)");
    console.log("Env Variable:", ENV_BASE || "(none)");
    try {
      const stored = localStorage.getItem("artibytes_auth_v1");
      if (stored) {
        const data = JSON.parse(stored);
        console.log("Authenticated:", !!data?.accessToken);
        console.log("User:", data?.user);
        console.log("Token Age:", data?.refreshedAt
          ? `${Math.floor((Date.now() - data.refreshedAt) / 60000)} minutes`
          : "unknown");
      } else {
        console.log("Authenticated: false");
      }
    } catch (e) {
      console.log("Auth status: error reading localStorage");
    }
    console.groupEnd();
  };
}

/* ==================== Utility Functions ==================== */

export async function readJSON(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    if (text.startsWith("<")) throw new Error("Backend response was HTML. Check your API configuration.");
    throw new Error("Invalid JSON from server.");
  }
}

export function normalizeListResponse(json) {
  if (Array.isArray(json)) return { items: json, nextCursor: null };
  return {
    items: Array.isArray(json?.items) ? json.items : [],
    nextCursor: json?.nextCursor || null,
  };
}

export function getImageUrl(item) {
  const url = item?.imageUrl || item?.cdnUrl || item?.resultUrl || null;
  if (!url) return null;
  if (url.startsWith("/api/") && BASE) return `${BASE}${url}`;
  return url;
}

export function getThumbUrl(item) {
  return item?.thumbUrl || getImageUrl(item);
}

export function fromNow(iso) {
  try {
    const d = new Date(iso);
    const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    const units = [
      ["yr", 31536000],
      ["mo", 2592000],
      ["d", 86400],
      ["h", 3600],
      ["m", 60],
      ["s", 1],
    ];
    for (const [u, v] of units) if (s >= v) return `${Math.floor(s / v)}${u} ago`;
  } catch {}
  return "";
}

export function shorten(id = "") {
  if (!id) return "";
  return id.length <= 12 ? id : `${id.slice(0, 6)}…${id.slice(-6)}`;
}

export function fmtPi(x) {
  if (!Number.isFinite(x)) return "— π";
  if (x >= 1) return `${x.toFixed(3)} π`;
  if (x >= 0.01) return `${x.toFixed(4)} π`;
  return `${x.toFixed(6)} π`;
}

export function toast(text) {
  const n = document.createElement("div");
  n.textContent = text;
  Object.assign(n.style, {
    position: "fixed",
    left: "50%",
    bottom: "28px",
    transform: "translateX(-50%)",
    background: "rgba(20,20,30,.92)",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: "10px",
    fontSize: "13px",
    zIndex: 9999,
    boxShadow: "0 8px 18px rgba(0,0,0,.25)",
    pointerEvents: "none",
  });
  document.body.appendChild(n);
  setTimeout(() => n.remove(), 1600);
}